import { useState } from "react";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FieldError, INPUT_CLASS } from "@/components/online/OnlineShell";
import { errorText, sendFeedback } from "@/lib/api";

export const SOURCES = ["IFLA", "Social Media", "From someone", "Other"];
const EMPTY = { message: "", name: "", city: "", country: "", email: "", source: "", source_other: "", website: "" };
const LABEL = "block text-[12px] font-semibold mb-1.5 text-[color:var(--ink-3)]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className={LABEL}>{label}</span>
      {children}
    </label>
  );
}

// Feedback form: only the message is needed; name, place, email and "where did you hear
// about it" are optional.
function FeedbackForm({ onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return setError("Write your feedback first");
    setBusy(true);
    setError("");
    try {
      await sendFeedback({ ...form, source: form.source || null, source_other: form.source === "Other" ? form.source_other : null });
      toast.success("Thank you for your feedback!");
      onDone();
    } catch (err) {
      setError(errorText(err, "Could not send your feedback. Please try again."));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
      <Field label="Feedback and recommendations">
        <Textarea data-testid="feedback-message-input" value={form.message} onChange={set("message")} maxLength={5000} rows={5} placeholder="What did you enjoy? What could be better?" className={`${INPUT_CLASS} h-auto min-h-[110px] text-[14px]`} autoFocus />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name (optional)">
          <Input data-testid="feedback-name-input" value={form.name} onChange={set("name")} maxLength={100} autoComplete="name" className={INPUT_CLASS} />
        </Field>
        <Field label="Email (optional)">
          <Input data-testid="feedback-email-input" type="email" value={form.email} onChange={set("email")} maxLength={200} autoComplete="email" className={INPUT_CLASS} />
        </Field>
        <Field label="City (optional)">
          <Input data-testid="feedback-city-input" value={form.city} onChange={set("city")} maxLength={100} autoComplete="address-level2" className={INPUT_CLASS} />
        </Field>
        <Field label="Country (optional)">
          <Input data-testid="feedback-country-input" value={form.country} onChange={set("country")} maxLength={100} autoComplete="country-name" className={INPUT_CLASS} />
        </Field>
      </div>
      <fieldset>
        <legend className={LABEL}>Where did you hear about Porosity? (optional)</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {SOURCES.map((s) => {
            const on = form.source === s;
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={on}
                data-testid={`feedback-source-${s.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setForm((f) => ({ ...f, source: on ? "" : s }))}
                className="h-9 px-3 rounded-[8px] text-[13px] font-medium cursor-pointer"
                style={{ border: `1px solid ${on ? "var(--sea)" : "var(--line)"}`, background: on ? "var(--sea)" : "var(--card)", color: on ? "#fff" : "var(--ink-2)" }}
              >
                {s}
              </button>
            );
          })}
        </div>
        {form.source === "Other" && (
          <Input data-testid="feedback-source-other-input" value={form.source_other} onChange={set("source_other")} maxLength={200} placeholder="Tell us where" aria-label="Where did you hear about Porosity" className={`${INPUT_CLASS} mt-2`} autoFocus />
        )}
      </fieldset>
      {/* Honeypot: hidden from people, so only bots fill it in. */}
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      <FieldError testId="feedback-error">{error}</FieldError>
      <div className="flex justify-end">
        <button type="submit" disabled={busy} className="btn-primary" data-testid="feedback-submit-btn">
          {busy ? "Sending…" : "Send feedback"}
        </button>
      </div>
    </form>
  );
}

// "Feedback" button plus its dialog. The form is remounted each time it opens, so it starts empty.
export default function FeedbackButton({ compact = false, className = "btn-ghost shrink-0", style }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} style={style} data-testid="feedback-btn" title="Send feedback" aria-label="Send feedback">
        <MessageSquare size={16} strokeWidth={1.8} /> {!compact && "Feedback"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="feedback-dialog"
          className="max-w-lg max-h-[calc(90*var(--svh))] overflow-y-auto"
          style={{ background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-card)" }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Send feedback</DialogTitle>
            <DialogDescription>Tell us what you think of Porosity, or suggest something we could improve.</DialogDescription>
          </DialogHeader>
          {open && <FeedbackForm onDone={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
